import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, MapPin, Users, ArrowRight } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip } from 'recharts';
// Radar chart data - Lidl vs Competitors
const radarData = [{
  category: 'Preis-Leistung',
  Lidl: 88,
  ALDI: 85,
  REWE: 72,
  EDEKA: 65
}, {
  category: 'Frische',
  Lidl: 82,
  ALDI: 78,
  REWE: 85,
  EDEKA: 88
}, {
  category: 'Freundlichkeit',
  Lidl: 79,
  ALDI: 75,
  REWE: 82,
  EDEKA: 85
}, {
  category: 'Sauberkeit',
  Lidl: 83,
  ALDI: 80,
  REWE: 85,
  EDEKA: 88
}, {
  category: 'Auswahl',
  Lidl: 75,
  ALDI: 70,
  REWE: 90,
  EDEKA: 92
}, {
  category: 'Erreichbarkeit',
  Lidl: 85,
  ALDI: 88,
  REWE: 80,
  EDEKA: 75
}];
// Win/Loss data
const competitorAnalysis = [{
  location: 'Hamburg-Hafencity',
  lidl: 'Lidl XL Hamburg',
  competitor: 'EDEKA Center',
  competitorType: 'EDEKA',
  distance: '0.8 km',
  status: 'winning',
  lidlRating: 4.6,
  competitorRating: 4.4,
  insight: 'Besseres Preis-Leistungs-Verhältnis',
  customerFlow: '+12%'
}, {
  location: 'Berlin-Kreuzberg',
  lidl: 'Lidl Berlin',
  competitor: 'ALDI Nord',
  competitorType: 'ALDI',
  distance: '0.3 km',
  status: 'losing',
  lidlRating: 3.8,
  competitorRating: 4.1,
  insight: 'Wartezeiten an der Kasse problematisch',
  customerFlow: '-8%'
}, {
  location: 'München-Schwabing',
  lidl: 'Lidl XL München',
  competitor: 'REWE',
  competitorType: 'REWE',
  distance: '1.2 km',
  status: 'winning',
  lidlRating: 4.8,
  competitorRating: 4.3,
  insight: 'Frische Backwaren überzeugen',
  customerFlow: '+18%'
}, {
  location: 'Köln-Ehrenfeld',
  lidl: 'Lidl Express Köln',
  competitor: 'Netto',
  competitorType: 'Netto',
  distance: '0.5 km',
  status: 'losing',
  lidlRating: 3.2,
  competitorRating: 3.8,
  insight: 'Sauberkeit verbessern',
  customerFlow: '-15%'
}, {
  location: 'Frankfurt-Sachsenhausen',
  lidl: 'Lidl XL Frankfurt',
  competitor: 'REWE',
  competitorType: 'REWE',
  distance: '0.6 km',
  status: 'neutral',
  lidlRating: 4.2,
  competitorRating: 4.2,
  insight: 'Gleichstand - Differenzierung durch Aktionen möglich',
  customerFlow: '0%'
}, {
  location: 'Düsseldorf-Bilk',
  lidl: 'Lidl XL Düsseldorf',
  competitor: 'ALDI Süd',
  competitorType: 'ALDI',
  distance: '0.4 km',
  status: 'winning',
  lidlRating: 4.3,
  competitorRating: 3.9,
  insight: 'Qualität und Backshop als Stärken',
  customerFlow: '+7%'
}];
const competitorColors: Record<string, string> = {
  REWE: '#cc071e',
  ALDI: '#00005f',
  EDEKA: '#003051',
  Netto: '#FFE700'
};
export function CompetitorRadarPage() {
  const winningCount = competitorAnalysis.filter(c => c.status === 'winning').length;
  const losingCount = competitorAnalysis.filter(c => c.status === 'losing').length;
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-395">
      {/* Header KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-396">
        <KPICard title="Marktposition" value="#1" change="Discounter" trend="up" icon={<Target size={18} data-id="element-398" />} subtext="in Europa" data-id="element-397" />
        <KPICard title="Gewonnene Standorte" value={winningCount} change={`von ${competitorAnalysis.length}`} trend="up" icon={<TrendingUp size={18} data-id="element-400" />} subtext="im 5km Radius" data-id="element-399" />
        <KPICard title="Verlorene Standorte" value={losingCount} change="Handlungsbedarf" trend="down" icon={<TrendingDown size={18} data-id="element-402" />} subtext="an Konkurrenz" data-id="element-401" />
        <KPICard title="Ø Rating-Vorsprung" value="+0.3" change="vs. Wettbewerb" trend="up" icon={<Users size={18} data-id="element-404" />} subtext="Sterne" data-id="element-403" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-405">
        {/* Radar Chart */}
        <Card data-id="element-406">
          <div className="p-6" data-id="element-407">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-408">
              Wettbewerbsvergleich
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-409">
              Kategorien-Analyse aus Google-Bewertungen
            </p>

            <div className="h-80" data-id="element-410">
              <ResponsiveContainer width="100%" height="100%" data-id="element-411">
                <RadarChart data={radarData} data-id="element-412">
                  <PolarGrid stroke="#e5e7eb" data-id="element-413" />
                  <PolarAngleAxis dataKey="category" tick={{
                  fontSize: 11,
                  fill: '#6b7280'
                }} data-id="element-414" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} data-id="element-415" />
                  <Radar name="Lidl" dataKey="Lidl" stroke="#0050AA" fill="#0050AA" fillOpacity={0.3} strokeWidth={2} data-id="element-416" />
                  <Radar name="ALDI" dataKey="ALDI" stroke="#00005f" fill="#00005f" fillOpacity={0.1} strokeWidth={2} data-id="element-417" />
                  <Radar name="REWE" dataKey="REWE" stroke="#cc071e" fill="#cc071e" fillOpacity={0.1} strokeWidth={2} data-id="element-418" />
                  <Legend wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-419" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} data-id="element-420" />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Legend explanation */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-id="element-421">
              <div className="text-xs text-gray-500 mb-2" data-id="element-422">
                Stärken von Lidl:
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-423">
                <Badge variant="success" data-id="element-424">Preis-Leistung: #1</Badge>
                <Badge variant="success" data-id="element-425">Erreichbarkeit: #1</Badge>
                <Badge variant="info" data-id="element-426">Sauberkeit: #2</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-3 mb-2" data-id="element-427">
                Verbesserungspotenzial:
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-428">
                <Badge variant="warning" data-id="element-429">Auswahl: #3</Badge>
                <Badge variant="warning" data-id="element-430">Freundlichkeit: #3</Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Category Breakdown */}
        <Card data-id="element-431">
          <div className="p-6" data-id="element-432">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-433">
              Kategorie-Details
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-434">
              Detaillierter Vergleich nach Bewertungskategorie
            </p>

            <div className="space-y-4" data-id="element-435">
              {radarData.map((item, index) => {
              const maxScore = Math.max(item.Lidl, item.ALDI, item.REWE);
              const isLidlLeading = item.Lidl === maxScore;
              return <motion.div key={item.category} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="space-y-2" data-id="element-436">
                    <div className="flex items-center justify-between" data-id="element-437">
                      <span className="text-sm font-medium text-gray-700" data-id="element-438">
                        {item.category}
                      </span>
                      {isLidlLeading && <Badge variant="success" className="text-xs" data-id="element-439">
                          Führend
                        </Badge>}
                    </div>
                    <div className="flex gap-2 h-6" data-id="element-440">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-441">
                        <div className="h-full bg-lidl-blue rounded-full transition-all duration-500" style={{
                      width: `${item.Lidl}%`
                    }} data-id="element-442" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600" data-id="element-443">
                          {item.Lidl}
                        </span>
                      </div>
                      <div className="w-16 text-xs text-gray-500 flex items-center" data-id="element-444">
                        Lidl
                      </div>
                    </div>
                    <div className="flex gap-2 h-4" data-id="element-445">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-446">
                        <div className="h-full bg-[#00005f] rounded-full opacity-60" style={{
                      width: `${item.ALDI}%`
                    }} data-id="element-447" />
                      </div>
                      <div className="w-16 text-xs text-gray-400" data-id="element-448">
                        ALDI {item.ALDI}
                      </div>
                    </div>
                    <div className="flex gap-2 h-4" data-id="element-449">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-450">
                        <div className="h-full bg-[#cc071e] rounded-full opacity-60" style={{
                      width: `${item.REWE}%`
                    }} data-id="element-451" />
                      </div>
                      <div className="w-16 text-xs text-gray-400" data-id="element-452">
                        REWE {item.REWE}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </Card>
      </div>

      {/* Win/Loss Table */}
      <Card data-id="element-453">
        <div className="p-6" data-id="element-454">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-455">
            Standort-Wettbewerb
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-456">
            Wo gewinnen oder verlieren wir Kunden im 5km-Radius?
          </p>

          <div className="overflow-x-auto" data-id="element-457">
            <table className="w-full" data-id="element-458">
              <thead data-id="element-459">
                <tr className="border-b border-gray-200" data-id="element-460">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-461">
                    Standort
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-462">
                    Lidl Markt
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-463">
                    Konkurrent
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-464">
                    Entfernung
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-465">
                    Ratings
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-466">
                    Kundenfluss
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-467">
                    Insight
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-468">
                {competitorAnalysis.map((item, index) => <motion.tr key={item.location} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className={`border-b border-gray-100 ${item.status === 'losing' ? 'bg-red-50/50' : item.status === 'winning' ? 'bg-green-50/50' : ''}`} data-id="element-469">
                    <td className="py-4 px-4" data-id="element-470">
                      <div className="flex items-center gap-2" data-id="element-471">
                        <MapPin size={14} className="text-gray-400" data-id="element-472" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-473">
                          {item.location}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4" data-id="element-474">
                      <span className="text-sm text-gray-700" data-id="element-475">{item.lidl}</span>
                    </td>
                    <td className="py-4 px-4" data-id="element-476">
                      <div className="flex items-center gap-2" data-id="element-477">
                        <div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: competitorColors[item.competitorType] || '#666'
                    }} data-id="element-478" />
                        <span className="text-sm text-gray-700" data-id="element-479">
                          {item.competitor}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-480">
                      <span className="text-sm text-gray-500" data-id="element-481">
                        {item.distance}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-482">
                      <div className="flex items-center justify-center gap-2" data-id="element-483">
                        <span className="text-sm font-semibold text-lidl-blue" data-id="element-484">
                          {item.lidlRating}
                        </span>
                        <ArrowRight size={14} className="text-gray-300" data-id="element-485" />
                        <span className="text-sm text-gray-500" data-id="element-486">
                          {item.competitorRating}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-487">
                      <span className={`text-sm font-semibold ${item.status === 'winning' ? 'text-green-600' : item.status === 'losing' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-488">
                        {item.customerFlow}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-489">
                      <span className="text-xs text-gray-500" data-id="element-490">
                        {item.insight}
                      </span>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}